import { IconButton } from "@material-ui/core";
import ListIcon from "@material-ui/icons/List";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { observer } from "mobx-react";
import * as React from "react";
import { getJobCategoryOptions, getKanbanStatusOptions } from "../../../constants";
import { t } from "../../../i18n/util";
import { ApplicationStateEnum } from "../../../network/APITypes";
import { applicationStore } from "../../../stores/ApplicationStore";
import { generalStore } from "../../../stores/GeneralStore";
import { hrStore } from "../../../stores/HrStore";
import { useTableStore } from "../../../stores/TableStore";
import { THEME } from "../../../theme/ThemeStore";
import { JobCategoryEnum } from "../../../types/models";
import { pushRoute } from "../../app/router/history";
import { ApplicationRoutes } from "../../application/router/ApplicationRoutes";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useTableSelection } from "../../hooks/useTableSelection";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { HrBotBackButton, HrBotButton, HrBotGreyButtonToggles } from "../../ui/Primitives";
import { DashboardList } from "../DashboardList";
import { BurgerMenu, MobileMenuElementContainer } from "../HrNavBarElements";
import { KanbanBoard } from "../KanbanBoard";
import { HrRoutes } from "../router/HrRoutes";

export const DashboardSite = observer(() => {
    const isMobile = useMobileMediaQuery();

    const tableStore = useTableStore("dashboard", {
        orderBy: "createdAt",
        orderDir: "asc"
    });

    const [openMobileFilterMenu, setOpenMobileFilterMenu] = React.useState(false);

    // saves where to go on "back" from details view
    React.useEffect(() => {
        hrStore.backTarget = HrRoutes.DASHBOARD;
    }, []);

    //#region handle job categories
    const selectJobCategory = useTableSelection(
        getJobCategoryOptions().map((jobCategoryOption) => ({ id: jobCategoryOption.value })),
        hrStore.selectedJobCategoryIds.length > 0 ? hrStore.selectedJobCategoryIds : "all"
    );

    React.useEffect(() => {
        // save filter to store in order to persist it
        hrStore.selectedJobCategoryIds = selectJobCategory.selectedIds;
    }, [selectJobCategory.selectedIds]);

    const memoizedJobCategories = React.useMemo(
        () => (!selectJobCategory.allSelected ? (selectJobCategory.selectedIds as JobCategoryEnum[]) : []),
        [selectJobCategory.allSelected, selectJobCategory.selectedIds]
    );

    const handleCloseJobCategoriesMenu = () => {
        selectJobCategory.selectedIds.length === 0 && selectJobCategory.toggleSelectAll();
    };
    //#endregion

    //#region handle application states
    const selectApplicationStatus = useTableSelection(
        getKanbanStatusOptions().map((status) => ({ id: status.value })),
        hrStore.selectedApplicationStates.length > 0 ? hrStore.selectedApplicationStates : "all"
    );

    React.useEffect(() => {
        // save filter to store in order to keep in memory after switching page
        hrStore.selectedApplicationStates = selectApplicationStatus.selectedIds;
    }, [selectApplicationStatus.selectedIds]);

    const memoizedApplicationStates = React.useMemo(
        () =>
            !selectApplicationStatus.allSelected ? (selectApplicationStatus.selectedIds as ApplicationStateEnum[]) : [],
        [selectApplicationStatus.allSelected, selectApplicationStatus.selectedIds]
    );

    const handleCloseApplicationStatusMenu = () => {
        selectApplicationStatus.selectedIds.length === 0 && selectApplicationStatus.toggleSelectAll();
    };
    //#endregion

    const handleAddApplicant = () => {
        applicationStore.reset();
        pushRoute(ApplicationRoutes.PERSONAL_DATA);
    };

    const handleSwitchViewType = (event: React.MouseEvent<HTMLElement>, newViewType: "kanban" | "list" | null) => {
        if (newViewType !== null) {
            hrStore.dashboardViewType = newViewType;
        }
    };

    const handleResetFilters = () => {
        !selectJobCategory.allSelected && selectJobCategory.toggleSelectAll();
        !selectApplicationStatus.allSelected && selectApplicationStatus.toggleSelectAll();
    };

    const tableHeader = isMobile ? (
        <>
            <div
                style={{
                    display: "flex",
                    paddingBottom: 16,
                    flexDirection: "column",
                    gap: 16
                }}
            >
                <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                    <tableStore.SearchField placeholder={t("screen.evidence.search")} />
                    <IconButton onClick={() => setOpenMobileFilterMenu(true)}>
                        <img src={THEME.Images.icFilter} alt="" />
                    </IconButton>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        justifyContent: "space-between",
                        height: 40
                    }}
                >
                    <ToggleButtonGroup
                        value={hrStore.dashboardViewType}
                        exclusive
                        onChange={handleSwitchViewType}
                        style={{ height: 40 }}
                    >
                        <ToggleButton value="kanban" style={{ width: 40 }}>
                            <ViewModuleIcon />
                        </ToggleButton>
                        <ToggleButton value="list" style={{ width: 40 }}>
                            <ListIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <img
                        src={THEME.Images.icAdd}
                        alt=""
                        style={{ width: 40, cursor: "pointer" }}
                        onClick={handleAddApplicant}
                    />
                </div>
            </div>
            <BurgerMenu open={openMobileFilterMenu} style={{ zIndex: 11, paddingTop: 0 }}>
                <MobileMenuElementContainer>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "0 16px"
                        }}
                    >
                        <div style={{ width: 24 }} />
                        <h3>{t("screen.evidence.filterOptions")}</h3>
                        <img
                            src={THEME.Images.icClose}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setOpenMobileFilterMenu(false);
                            }}
                        />
                    </div>
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotGreyButtonToggles
                        sorted
                        descriptionText={t("screen.dashboard.division")}
                        onClose={handleCloseJobCategoriesMenu}
                        options={getJobCategoryOptions()}
                        select={selectJobCategory}
                    />
                </MobileMenuElementContainer>
                {hrStore.dashboardViewType === "list" && (
                    <MobileMenuElementContainer>
                        <HrBotGreyButtonToggles
                            descriptionText={t("screen.dashboard.status")}
                            onClose={handleCloseApplicationStatusMenu}
                            options={getKanbanStatusOptions()}
                            select={selectApplicationStatus}
                        />
                    </MobileMenuElementContainer>
                )}
                <MobileMenuElementContainer>
                    <HrBotButton
                        label={t("screen.evidence.saveFilterOptions")}
                        type="button"
                        style={{ width: "fit-content" }}
                        onClick={() => setOpenMobileFilterMenu(false)}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotBackButton
                        label={t("screen.evidence.resetFilterOptions")}
                        style={{ width: "fit-content", padding: "18px 26px" }}
                        onClick={() => {
                            setOpenMobileFilterMenu(false);
                            handleResetFilters();
                        }}
                    />
                </MobileMenuElementContainer>
            </BurgerMenu>
        </>
    ) : (
        <div
            style={{
                paddingLeft: 20,
                marginBottom: 38,
                gap: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            }}
        >
            <div style={{ display: "flex", gap: 16 }}>
                <HrBotGreyButtonToggles
                    sorted
                    descriptionText={t("screen.dashboard.division")}
                    onClose={handleCloseJobCategoriesMenu}
                    options={getJobCategoryOptions()}
                    select={selectJobCategory}
                />
                {hrStore.dashboardViewType === "list" && (
                    <HrBotGreyButtonToggles
                        descriptionText={t("screen.dashboard.status")}
                        onClose={handleCloseApplicationStatusMenu}
                        options={getKanbanStatusOptions()}
                        select={selectApplicationStatus}
                    />
                )}
            </div>

            <div style={{ display: "flex", gap: 16, height: 40, justifyContent: "flex-end", flexGrow: 1 }}>
                <ToggleButtonGroup value={hrStore.dashboardViewType} exclusive onChange={handleSwitchViewType}>
                    <ToggleButton value="kanban" style={{ width: 40 }}>
                        <ViewModuleIcon />
                    </ToggleButton>
                    <ToggleButton value="list" style={{ width: 40 }}>
                        <ListIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
                <tableStore.SearchField placeholder={t("screen.evidence.search")} maxWidth />

                <img
                    src={THEME.Images.icAdd}
                    alt=""
                    style={{ width: 40, cursor: "pointer", marginRight: 14 }}
                    onClick={handleAddApplicant}
                />
            </div>
        </div>
    );

    return (
        <div
            style={{
                padding: isMobile ? 20 : 36,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column"
            }}
        >
            {tableHeader}
            <div
                // id ist used by sticky column headers to prevent them from scrolling
                id="stickyParent"
                style={{
                    display: "flex",
                    position: "relative",
                    flexGrow: 1,
                    overflow: "auto",
                    flexDirection: "column"
                }}
            >
                {hrStore.dashboardViewType === "kanban" && (
                    <KanbanBoard jobCategories={memoizedJobCategories} tableStore={tableStore} />
                )}
                {hrStore.dashboardViewType === "list" && (
                    <ContentWrapper
                        style={
                            !generalStore.hideNavigation
                                ? { padding: isMobile ? 0 : "0 56px", maxWidth: 1600, flexGrow: 1 }
                                : { flexGrow: 1 }
                        }
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 50,
                                gap: 32
                            }}
                        >
                            <DashboardList
                                jobCategories={memoizedJobCategories}
                                applicationStates={memoizedApplicationStates}
                                tableStore={tableStore}
                            />

                            {tableStore.totalCount !== 0 && <tableStore.Pagination isMobile={isMobile} />}
                        </div>
                    </ContentWrapper>
                )}
            </div>
        </div>
    );
});
